
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import TestSelector from "@/components/level1/TestSelector";
import TestInterface from "@/components/level1/TestInterface";
import { useLevel1Training } from "@/hooks/level1/useLevel1Training";
import { hasSavedExerciseData } from "@/hooks/level3/useAutoSave";
import { 
  puttingTests,
  chippingTests,
  wedgingTests,
  bunkerTests,
  ironTests,
  fairwayWoodTests,
  driverTests,
  levels
} from "@/data/level1/tests";
import { getFormattedPoint } from "@/components/level1/scoring/utils";
import { getTableHeadTitle } from "@/utils/level3/titles";
import { getTestTitle, getTestDescription, getTestPoints, getTestDistances } from "@/services/Level1Service";
import { useFavorites } from "@/hooks/useFavorites";
import NavigationMenu from "@/components/layout/NavigationMenu";

const Level1Training = () => {
  const { t } = useTranslation('training');
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshFavorites } = useFavorites();
  
  const {
    scores,
    ballTendencies,
    selectedTest,
    currentPoint,
    currentAttempt,
    handleScoreChange,
    cancelLastEntry,
    handleFinish,
    handleSelectTest,
    handleBackToTests,
    getPointTotal,
    getGrandTotal
  } = useLevel1Training();

  const getTableHead = () => getTableHeadTitle(selectedTest);
  
  // Debug logs pour vérifier les points et distances
  useEffect(() => {
    if (selectedTest) {
      const points = getTestPoints(selectedTest);
      const distances = getTestDistances(selectedTest);
      console.log("Level1Training - Test sélectionné:", selectedTest);
      console.log("Level1Training - Points pour ce test:", points);
      console.log("Level1Training - Distances pour ce test:", distances);
      console.log("Level1Training - Point actuel:", currentPoint);
    }
  }, [selectedTest, currentPoint]);
  
  // Ne charger depuis l'URL que s'il n'y a pas de données sauvegardées
  useEffect(() => {
    const testParam = searchParams.get('test');
    const { hasData } = hasSavedExerciseData();
    
    // Si des données sont sauvegardées, ne pas interférer
    if (hasData) {
      console.log('Données sauvegardées détectées, restauration en cours...');
      return;
    }
    
    if (testParam && !selectedTest) {
      const allTests = [...puttingTests, ...chippingTests, ...wedgingTests, ...bunkerTests, ...ironTests, ...fairwayWoodTests, ...driverTests];
      const testExists = allTests.some(test => test.id === testParam);
      
      if (testExists) {
        handleSelectTest(testParam);
      }
    }
  }, [searchParams, selectedTest, handleSelectTest]);
  
  useEffect(() => {
    if (selectedTest) {
      setSearchParams({ test: selectedTest });
    } else {
      setSearchParams({});
    }
  }, [selectedTest, setSearchParams]);

  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  const handleBackClick = () => {
    console.log('Navigating back to test selection from Level1Training');
    handleBackToTests();
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="container mx-auto py-8">
        <div className="flex justify-between mb-4">
          <div className="text-golf-600">
            <Link to="/training" className="text-green-600 hover:text-green-700 flex items-center gap-2">
              <ChevronLeft className="h-6 w-6" />
              <span>{t('levels.level1.interface.backToTraining')}</span>
            </Link>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-golf-800 mb-4">{t('levels.level1.interface.title')}</h1>
          <p className="text-xl text-golf-600">{t('levels.level1.interface.objective')}</p>
        </motion.div>

        {selectedTest === null ? (
          <TestSelector 
            puttingTests={puttingTests}
            chippingTests={chippingTests}
            wedgingTests={wedgingTests}
            bunkerTests={bunkerTests}
            ironTests={ironTests}
            fairwayWoodTests={fairwayWoodTests}
            driverTests={driverTests}
            onSelectTest={handleSelectTest}
          />
        ) : (
          <TestInterface 
            selectedTest={selectedTest}
            currentPoint={currentPoint}
            currentAttempt={currentAttempt}
            scores={scores}
            onBackToTests={handleBackClick}
            onFinish={handleFinish}
            onScoreChange={handleScoreChange}
            onCancelLastEntry={cancelLastEntry}
            getTestTitle={getTestTitle}
            getTestDescription={getTestDescription}
            getTestPoints={getTestPoints}
            getTestDistances={getTestDistances}
            getTableHeadTitle={getTableHead}
            getFormattedPoint={getFormattedPoint}
            getPointTotal={getPointTotal}
            getGrandTotal={getGrandTotal}
            levels={levels}
          />
        )}
      </div>
    </div>
  );
};

export default Level1Training;
